import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
//mui
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
//icons
import CloseIcon from "@mui/icons-material/Close";
//styles
import styles from "../styles/dashboard.module.css";
import dashboardStore from "../store/dashboardStore";
//翻譯
import { useTranslation } from "react-i18next";

const ISO_LABELS = [
  { key: "1.1", label: "1.1 Stationary Combustion" },
  { key: "1.2", label: "1.2 Mobile Combustion" },
  { key: "1.3", label: "1.3 Industrial Processes" },
  { key: "1.4", label: "1.4 Fugitive Emissions" },
  {
    key: "1.5",
    label: "1.5 Land Use, Land Use Change and Forestry (LULUCF)",
  },
  { key: "2.1", label: "2.1 Imported Electricity" },
  { key: "2.2", label: "2.2 Imported Energy" },
  { key: "3.1", label: "3.1 Upstream Transport and Distribution for Goods" },
  {
    key: "3.2",
    label: "3.2 Downstream Transport and Distribution for Goods",
  },
  { key: "3.3", label: "3.3 Employee Commuting" },
  { key: "3.4", label: "3.4 Client and Visitor Transport" },
  { key: "3.5", label: "3.5 Business Travel" },
  { key: "4.1", label: "4.1 Goods Purchased by an Organization" },
  {
    key: "4.2",
    label:
      "4.2 Capital Goods that are Purchased and Amortized by an Organization",
  },
  {
    key: "4.3",
    label:
      "4.3 Disposal Services (of solid or liquid waste) Used by an Organization",
  },
  {
    key: "4.4",
    label: "4.4 Use of Assets (through leased equipment) by an Organization",
  },
  {
    key: "4.5",
    label:
      "4.5 Use of Services not Described in the above sub-categories by an Organization",
  },
  { key: "5.1", label: "5.1 Use Stage of the Product" },
  { key: "5.2", label: "5.2 Downstream Leased Assets" },
  { key: "5.3", label: "5.3 End of Life Stage of the Product" },
  { key: "5.4", label: "5.4 Investments" },
  {
    key: "6.1",
    label: "6.1 Indirect GHG Emissions from other sources",
  },
];

const GHG_LABELS = [
  { key: "1.1", label: "1.1 Direct Emissions from Stationary Combustion" },
  { key: "1.2", label: "1.2 Direct Emissions from Mobile Combustion" },
  { key: "1.3", label: "1.3 Direct Emissions from Process Sources" },
  { key: "1.4", label: "1.4 Direct Emissions from Fugitive Sources" },
  { key: "1.5", label: "1.5 Direct Emissions from Agricultural Sources" },
  {
    key: "2.1",
    label: "2.1 Indirect Emissions from Purchased/Acquired Electricity",
  },
  { key: "2.2", label: "2.2 Indirect Emissions from Purchased/Acquired Steam" },
  {
    key: "2.3",
    label: "2.3 Indirect Emissions from Purchased/Acquired Heating",
  },
  {
    key: "2.4",
    label: "2.4 Indirect Emissions from Purchased/Acquired Cooling",
  },
  { key: "3.1", label: "3.1 Purchased Goods and Services" },
  { key: "3.2", label: "3.2 Capital Goods" },
  {
    key: "3.3",
    label:
      "3.3 Fuel and Energy-related Activities(not included in Scope 1 or Scope 2)",
  },
  { key: "3.4", label: "3.4 Upstream Transportation and Distribution" },
  { key: "3.5", label: "3.5 Waste Generated in Operations" },
  { key: "3.6", label: "3.6 Business Travel" },
  { key: "3.7", label: "3.7 Employee Commuting" },
  { key: "3.8", label: "3.8 Upstream Leased Assets" },
  { key: "3.9", label: "3.9 Downstream Transportation and Distribution" },
  { key: "3.10", label: "3.10 Processing of Solid Products" },
  { key: "3.11", label: "3.11 Use of Solid Products" },
  { key: "3.12", label: "3.12 End-of-Life Treatment of Solid Products" },
  { key: "3.13", label: "3.13 Downstream Leased Assets" },
  { key: "3.14", label: "3.14 Franchise (Scope 1 and Scope 2)" },
  { key: "3.15", label: "3.15 Investments" },
];

const MyEmissionHotpotsModal = observer(({ open, setIsOpen }) => {
  const { t } = useTranslation("ActivityMain");

  const handleClose = () => {
    setIsOpen(false);
  };

  const unitLabel =
    dashboardStore.unit === "20"
      ? "kgCO2e"
      : dashboardStore.unit === "21"
        ? "tCO2e"
        : "";

  const xLabels = dashboardStore.standardId === 1 ? ISO_LABELS : GHG_LABELS;

  const currentColors = useMemo(() => {
    return xLabels.map((item) => {
      // 修改點：因為 xLabels 現在是物件陣列，所以要取 item.key
      const keyStr = item.key;

      const scopeIndex = parseInt(keyStr.split(".")[0]) - 1; // "1.1" -> 0, "2.1" -> 1
      // 防止索引超出 themeColor 範圍
      const safeIndex = Math.min(
        scopeIndex,
        dashboardStore.themeColor.length - 1,
      );
      return dashboardStore.themeColor[safeIndex];
    });
  }, [xLabels, dashboardStore.themeColor]);

  function LinearProgressWithLabel({ value, title, percentage, color }) {
    return (
      <div className={styles.linearProgressWrapperModal}>
        {/* title */}
        <div className={styles.progressTitleModal}>{t(title)}</div>

        {/* lineBar */}
        <div className={styles.progressBarModal}>
          <LinearProgress
            variant="determinate"
            value={parseFloat(percentage)}
            sx={{
              height: 20,
              borderRadius: 5,
              "& .MuiLinearProgress-bar": {
                //  backgroundColor: "#4caf50", // 綠色
                backgroundColor: color || "#4caf50", // 預設顏色
              },
              backgroundColor: "#ccc",
            }} //客製化調整Line寬度
          />
          <div className={styles.progressPercentageOnBar}>{percentage}</div>
        </div>

        {/* value and unit */}
        <div className={styles.valueUnitWrapperModal}>
          <div className={styles.progressValue}>
            {Number(value).toLocaleString()}
          </div>
          <div className={styles.progressUnit}>{unitLabel}</div>
        </div>
      </div>
    );
  }

  const sortedData =
    dashboardStore.barChartData &&
    Object.entries(dashboardStore.barChartData)
      .map(([key, data]) => ({ key, ...data }))
      .sort((a, b) => b.value - a.value) // 根據數值從大到小排序
      .map((item) => {
        const labelKey = item.key.replace("_", "."); // e.g. "1_2" → "1.2"
        const match = xLabels.find((x) => x.key === labelKey);
        const index = xLabels.findIndex((x) => x.key === labelKey);

        return {
          title: match?.label || labelKey, // 找不到 fallback 使用 key
          value: item.value,
          percentage: `${item.percentage}%`,
          color: currentColors[index] || "#ccc", // 找不到就給預設顏色
        };
      });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          height: "600px",
        },
      }}
    >
      <DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {t("My Emission Hotpots")}
      </DialogTitle>
      <Divider />
      <DialogContent>
        {/* 進度條 */}
        <div className={styles.linearProgressArea}>
          {sortedData.map((item, index) => (
            <LinearProgressWithLabel
              key={index}
              // value={parseFloat(item.percentage)}
              value={item.value}
              title={item.title}
              percentage={item.percentage}
              color={item.color}
            />
          ))}
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default MyEmissionHotpotsModal;
